/**
 * Accommodation Types Component Registry
 * 
 * This module serves as a centralized registry for accommodation type-specific components.
 * It provides a factory pattern implementation that returns the appropriate component
 * based on the accommodation type, allowing for extensible and type-specific UI rendering.
 */

import React from 'react';
import HotelDetails from './HotelDetails';
import DefaultDetails from './DefaultDetails';

/**
 * Component Factory Function - getTypeComponent
 * 
 * PURPOSE:
 * This function implements the factory pattern to return the appropriate React component
 * for rendering details based on the accommodation type.
 * 
 * HOW IT WORKS:
 * 1. Takes an accommodation type string as input
 * 2. Uses a switch statement to map the type to its specialized component
 * 3. Falls back to a default component if the type is not recognized
 * 
 * EXTENSIBILITY:
 * This pattern allows easy extension by adding new case statements when new
 * accommodation types are implemented, without modifying existing code (Open/Closed Principle).
 * The commented cases show planned future accommodation types.
 * 
 * @param {string} type - The accommodation type (e.g., 'hotel', 'apartment')
 * @returns {React.Component} The appropriate component for rendering that accommodation type
 */
export const getTypeComponent = (type) => {
  switch (type) {
  case 'hotel':
    return HotelDetails;
    // Add other accommodation types as they are implemented
    // case 'apartment':
    //     return ApartmentDetails;
    // case 'house':
    //     return HouseDetails;
    // case 'resort':
    //     return ResortDetails;
    // case 'hostel':
    //     return HostelDetails;
  default:
    return DefaultDetails;
  }
};

/**
 * Default Module Export
 * 
 * PURPOSE:
 * Exports all available detail components as a single object for convenient imports.
 * 
 * USAGE:
 * This allows consumers to import specific components directly:
 * import { HotelDetails } from './types';
 * 
 * Or import the entire object:
 * import AccommodationTypes from './types';
 * const { HotelDetails } = AccommodationTypes;
 */
export default {
  HotelDetails,
  DefaultDetails
}; 